import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConversationMessageView } from '@mark43/cad-api';
import { useMessagingResource } from './use-messaging-resource';
import { ListStore } from '@list-store';
import { useEffect, useMemo } from 'react';
import { StreamMap } from '@m43/core';
import { SocketListener } from '@m43/sockets-mark43';
import { useSocketStatus } from '../../../socket/use-stream';
import { MessagingStore } from '../../store/messaging-store';
import { useRateLimitMonitor } from '../../../common/hooks/use-rate-limit-monitor';

export const useConversationMessages = (store: MessagingStore) => {
    const {
        areConversationsFetched,
        clearNewestMessageForConversation,
        conversationMetadata,
        selectedConversation,
        selectedConversationKey,
    } = store;

    const { isScrolledToBottom, lastReceivedMessageId: storedLastReceivedMessageId } =
        conversationMetadata ?? {};

    const { newestMessageId } = selectedConversation ?? {};

    const { getMessagesForConversation } = useMessagingResource();
    const queryKey = [
        'conversation-messages',
        selectedConversationKey && selectedConversationKey.toString(),
    ];
    const queryClient = useQueryClient();

    const getMessagesForConversationQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (selectedConversationKey === undefined) {
                throw new Error('conversationKey is undefined');
            }

            const { messageList } = queryClient.getQueryData<{
                messageList: ListStore<ConversationMessageView>;
            }>(queryKey) || { messageList: new ListStore<ConversationMessageView>() };

            const existingMessages = messageList.data.list;

            // `lastReceivedMessageId` is sent when making API call to get messages for a conversation and the BE
            // returns only messages newer than that message id.
            //
            // If there are no previously fetched messages stored then we request the backend to send all messages.
            // Note this could be because it's the first time retrieving messages for a conversation, or it could be
            // because the messages cache for this conversation was cleared.
            //
            // If there are previously fetched messages stored then we request the BE to send all messages newer than
            //   `newestMessageId`. We use this field (stored on the ConversationView) rather than just looking at the
            // last message in the messageList to avoid a possible race condition.
            //
            // The race condition is as follows:
            //
            //  1. A new message nudge was sent but not yet received by the user
            //  2. The user creates a message and the response arrives before the new message nudge
            //  3. The user's ConversationMessageView object is added to the messageList
            //  4. The new message nudge arrives (it doesn't have a message id, only a conversation id)
            //  5. The user requests all messages after the last message in their store (which was the message they created)
            //  6. The message which triggered the new message nudge would never be fetched.
            //
            // Since we only use the ids of messages fetched to determine which was the last message received (and not the
            // message id from the create message response), this problem can not occur.

            const lastReceivedMessageId =
                existingMessages === undefined || existingMessages.length === 0
                    ? undefined
                    : storedLastReceivedMessageId;

            const newMessages = await getMessagesForConversation({
                conversationKey: selectedConversationKey,
                lastReceivedMessageId,
            });

            if (newMessages.length === 0) {
                // The empty get messages response means that this conversation has timed out due to inactivity.
                // So we clear the newest message is for this conversation which will stop this query from retrying.
                // If a nudge comes in with a new message it will update the newestMessageId and this query will
                // retry.
                clearNewestMessageForConversation(selectedConversationKey.conversationId);
            } else {
                const previousMessageCount = messageList.data.list.length;
                messageList.upsertItems(newMessages);
                const newestMessage = newMessages[newMessages.length - 1];

                if (previousMessageCount === 0 || isScrolledToBottom) {
                    // scroll messages to bottom when new messages are fetched if there were previously
                    // no messages fetched or if the messages subpanel was previously scrolled to the bottom
                    const newestMessageIndex = messageList.getItemIndex(newestMessage.id);
                    if (newestMessageIndex !== undefined && selectedConversationKey !== undefined) {
                        store.setConversationScrollToIndex(
                            selectedConversationKey,
                            newestMessageIndex
                        );
                    }
                }

                store.setLastReceivedMessageId(selectedConversationKey, newestMessage.id);
            }

            return { messageList }; // we return a new constructed object to avoid mutation only update
        },
        enabled: false,
        cacheTime: Infinity, // TODO make cache smaller
        staleTime: Infinity,
    });

    const { isError, refetch, isFetching } = getMessagesForConversationQuery;

    useEffect(() => {
        store.setConversationMessagesFetchState({ isError, isFetching, refetch });
    }, [isError, isFetching, refetch, store]);

    // TODO: RND-15373
    const rateLimitMonitorForSocketStatusChangeRefetch = useRateLimitMonitor(
        'socketStatusChangeRefetch',
        1000
    );
    // TODO: RND-15373
    const rateLimitMonitorForNewMessageAvailableRefetch = useRateLimitMonitor(
        'newMessageAvailableRefetch',
        1000
    );

    useEffect(
        () => {
            // This useEffect fetches more messages for the currently selected conversation if there
            // is a message newer than the latest message already fetched.
            if (
                areConversationsFetched &&
                selectedConversationKey !== undefined &&
                !selectedConversationKey.isNewConversationKey() &&
                !selectedConversationKey.isBookmarkedConversationKey() &&
                !isError &&
                !isFetching &&
                newestMessageId !== undefined &&
                (storedLastReceivedMessageId === undefined ||
                    newestMessageId > storedLastReceivedMessageId)
            ) {
                rateLimitMonitorForNewMessageAvailableRefetch.incrementCount({
                    areConversationsFetched,
                    selectedConversationKeyNotUndefined: selectedConversationKey !== undefined,
                    selectedConversationIsNotNew: !selectedConversationKey.isNewConversationKey(),
                    selectedConversationIsNotBookmarkedConversation: !selectedConversationKey.isBookmarkedConversationKey(),
                    isNotError: !isError,
                    isNotFetching: !isFetching,
                    newestMessageIdIsDefined: newestMessageId !== undefined,
                    storedLastReceivedMessageIdIsUndefined:
                        storedLastReceivedMessageId === undefined,
                    newestMessageIdGreaterThanStoredLastReceivedMessageId:
                        storedLastReceivedMessageId === undefined ||
                        newestMessageId > storedLastReceivedMessageId,
                });
                refetch();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            areConversationsFetched,
            selectedConversationKey,
            isError,
            isFetching,
            newestMessageId,
            storedLastReceivedMessageId,
            refetch,
        ]
    );

    // Refetch messages if websocket re-connects
    const socketListener: SocketListener<StreamMap> = useMemo(
        () => ({
            invokeOnInitialize: true,
            onSocketStatusChange: ({ status }) => {
                if (
                    status === 'CONNECTED' &&
                    selectedConversationKey &&
                    !selectedConversationKey.isNewConversationKey() &&
                    !selectedConversationKey.isBookmarkedConversationKey()
                ) {
                    rateLimitMonitorForSocketStatusChangeRefetch.incrementCount();
                    getMessagesForConversationQuery.refetch();
                }
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    useSocketStatus(socketListener);

    return getMessagesForConversationQuery;
};
